import { ref, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

export default function useCoursePermissionsTab() {
  const store = useStore();
  const route = useRoute();
  const courseId = route.params.id;

  const isLoading = computed(
    () => store.getters["course/isLoadingPermissions"]
  );
  const coursePermissions = computed(
    () => store.getters["course/getPermissions"]
  );
  const message = computed(() => store.getters["course/getMessagePermissions"]);
  const errors = computed(() => store.getters["course/getErrorsPermissions"]);

  const renderedPermissions = ref([]);
  const attributeChanged = ref(null);

  onMounted(async () => {
    await store.dispatch("course/fetchPermissions", courseId);
    renderedPermissions.value = store.getters["course/getPermissions"];
  });

  const formModel = computed(() => {
    // make form reactive
    const res = renderedPermissions.value.map((pkg) => ({
      packageName: pkg.name,
      active: ref(
        !pkg.modules.map((mod) => mod.active).some((active) => !active)
      ),
      modules: pkg.modules.map((mod) => ({
        moduleName: mod.name,
        active: ref(mod.active),
        permissions: mod.permissions.map((perm) => ({
          permissionName: perm.name,
          active: ref(perm.active),
          id: perm.id,
        })),
      })),
    }));
    return res;
  });

  // Watch for package checkbox changes and update modules & permissions
  watch(
    formModel,
    (newVal, oldVal) => {
      if (!oldVal) return;
      newVal.forEach((pkg) => {
        if (attributeChanged.value === "package") {
          if (pkg.active.value) {
            // If package is checked, check all modules and permissions
            pkg.modules.forEach((mod) => {
              mod.active.value = true;
              mod.permissions.forEach((perm) => {
                perm.active.value = true;
              });
            });
          } else {
            // If package is unchecked, uncheck all modules and permissions
            pkg.modules.forEach((mod) => {
              mod.active.value = false;
              mod.permissions.forEach((perm) => {
                perm.active.value = false;
              });
            });
          }
        } else if (attributeChanged.value === "module") {
          pkg.modules.forEach((mod) => {
            if (mod.active.value) {
              // If module is checked, check all permissions
              mod.permissions.forEach((perm) => {
                perm.active.value = true;
              });
            } else {
              // If module is unchecked, uncheck all permissions
              mod.permissions.forEach((perm) => {
                perm.active.value = false;
              });
            }
          });

          // If all modules are checked, check the package
          pkg.active.value = pkg.modules.every((mod) => mod.active.value);
        } else if (attributeChanged.value === "permission") {
          // Check if all permissions in the module are checked
          pkg.modules.forEach((mod) => {
            mod.active.value = mod.permissions.every(
              (perm) => perm.active.value
            );
          });
          // Check if all modules in the package are checked
          pkg.active.value = pkg.modules.every((mod) => mod.active.value);
        }
      });
    },
    { deep: true }
  );

  watch(message, (newValue) => {
    if (newValue != null) {
      store.dispatch("toastNotification/displayToast", {
        message: newValue,
        type: "success",
      });
    }
  });

  watch(errors, (newValue) => {
    if (newValue != null) {
      store.dispatch("toastNotification/displayToast", {
        message: newValue,
        type: "error",
      });
    }
  });

  const toggleModule = [];

  const permissionModules = {
    go: "GO PERMISSIONS",
    track: "TRACK PERMISSIONS",
    anly_reporting: "ANLY & REPORTING PERMISSIONS",
    player: "PLAYER EXPERIENCE MODULE",
    fleet: "FLEET / GEOFENCE / ALERTS MODULES",
    twoway: "2WAY SPECIAL FEATURES",
    custom: "CUSTOM FEATURES",
  };

  const getModuleTitle = (moduleName) => permissionModules[moduleName];

  const togglePackage = (name) => {
    // find package
    const selectedPackage = coursePermissions.value.find(
      (element) => element.name === name
    );
    const newValue = !selectedPackage.active;
    // api call
    const payload = {
      courseId: courseId,
      packageName: name,
      active: newValue,
    };
    store.dispatch("course/updatePackage", payload);
    // uses should be reset after this?
  };

  const apiUpdatePermissions = (permissions) => {
    if (!courseId) {
      // Course ID not available
      return;
    }
    store.dispatch("course/updatePermissions", {
      courseId: courseId,
      permissions,
    });
  };

  function getPermissionInputClass(active) {
    return active.value ? "bg-primary" : "bg-gray";
  }

  function handleSubmit() {
    // get array of active permission ids
    const permissions = [];
    formModel.value.forEach((pkg) => {
      pkg.modules.forEach((mod) => {
        mod.permissions.forEach((perm) => {
          if (perm.active.value) {
            permissions.push(perm.id);
          }
        });
      });
    });
    apiUpdatePermissions(permissions);
  }

  function onTogglePackage(pkg) {
    attributeChanged.value = "package";
    pkg.active.value = !pkg.active.value;
  }
  function onToggleModule(mod) {
    attributeChanged.value = "module";
    mod.active.value = !mod.active.value;
  }
  function onTogglePermission(perm) {
    attributeChanged.value = "permission";
    perm.active.value = !perm.active.value;
  }

  function onCreateUpdateTmUser() {
    store.dispatch("course/createUpdateTmUser", courseId);
  }

  return {
    isLoading,
    permissionModules,
    getModuleTitle,
    togglePackage,
    toggleModule,
    coursePermissions,
    formModel,
    getPermissionInputClass,
    attributeChanged,
    handleSubmit,
    onTogglePackage,
    onToggleModule,
    onTogglePermission,
    onCreateUpdateTmUser,
  };
}
